import { useWorkspace } from '@/features/workspaces/hooks';
import { PerformanceAudienceFilterOption, useGetCampaignPerformanceDashboardQuery } from '@/services/endpoints/performanceAudience';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PerformanceAudienceFiltersState } from '../types';
import {
  buildCampaignPerformanceDashboardArgs,
  canBuildDashboardArgs,
  isAllAccountsScopeId,
  parseAccountCountFromSuffix,
} from '../utils/buildCampaignPerformanceDashboardArgs';
import { mapCampaignPerformanceDashboard } from '../utils/mapCampaignPerformanceDashboard';

export type PerformanceDashboardDataContext = {
  audiences: PerformanceAudienceFilterOption[];
  googleScopes: PerformanceAudienceFilterOption[];
  metaScopes: PerformanceAudienceFilterOption[];
};

export const usePerformanceDashboardData = (filters: PerformanceAudienceFiltersState, enabled: boolean, context: PerformanceDashboardDataContext) => {
  const { id: workspaceId } = useWorkspace();
  const { i18n } = useTranslation('performanceAudience');

  const scopeOptions = useMemo(
    () => ({
      audiences: context.audiences,
      googleScopes: context.googleScopes,
      metaScopes: context.metaScopes,
    }),
    [context.audiences, context.googleScopes, context.metaScopes]
  );

  const hasAudienceAndSources = context.audiences.some((option) => option.id === filters.audienceId) && canBuildDashboardArgs(filters, scopeOptions);

  const shouldFetch = !!workspaceId && enabled && hasAudienceAndSources;

  const dashboardArgs = useMemo(() => {
    if (!workspaceId || !hasAudienceAndSources) {
      return null;
    }

    return buildCampaignPerformanceDashboardArgs(workspaceId, filters, scopeOptions);
  }, [workspaceId, filters, scopeOptions, hasAudienceAndSources]);

  const { data, isLoading, isFetching, isError, refetch } = useGetCampaignPerformanceDashboardQuery(dashboardArgs!, {
    skip: !shouldFetch || !dashboardArgs,
  });

  const mapped = useMemo(() => {
    const selectedAudience = context.audiences.find((option) => option.id === filters.audienceId);
    const selectedGoogle = context.googleScopes.find((option) => option.id === filters.googleScopeId);

    const googleAccountCount =
      parseAccountCountFromSuffix(selectedGoogle?.suffix) ??
      (selectedGoogle && !isAllAccountsScopeId(selectedGoogle.id) ? 1 : context.googleScopes.filter((o) => !isAllAccountsScopeId(o.id)).length);

    const realMetaCount = context.metaScopes.filter((o) => !isAllAccountsScopeId(o.id)).length;
    const metaAdAccountCount = filters.metaScopeIds.length || realMetaCount;

    return mapCampaignPerformanceDashboard(data, {
      audienceName: selectedAudience?.value ?? '',
      googleAccountCount,
      metaAdAccountCount,
      locale: i18n.language,
    });
  }, [data, context.audiences, context.googleScopes, context.metaScopes, filters.audienceId, filters.googleScopeId, filters.metaScopeIds, i18n.language]);

  const isInitialLoading = shouldFetch && isLoading && !data;
  const isRefetching = shouldFetch && isFetching && !!data;

  const refetchDashboard = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    summary: mapped?.summary,
    kpis: mapped?.kpis ?? [],
    trend: mapped?.trend,
    comparison: mapped?.comparison ?? [],
    storeVisits: mapped?.storeVisits,
    isLoading: isInitialLoading,
    isRefetching,
    isError: shouldFetch && isError,
    refetch: refetchDashboard,
    hasData: !!mapped,
  };
};
